import React, { useState, useEffect } from 'react';
import { 
  createInventoryTransaction, 
  getWarehouseInventory 
} from '../../services/inventoryService';
import { getWarehouses } from '../../services/productService';
import { Warehouse, Unit } from '../../types/product';
import { TransactionType } from '../../types/inventory';
import './InventoryTransactionForm.css';

interface InventoryTransactionFormProps {
  productId: string;
  productName: string;
  variantId?: string;
  variantAttributes?: Record<string, string>;
  unitId: string;
  unitName: string;
  onTransactionComplete: () => void;
}

const InventoryTransactionForm: React.FC<InventoryTransactionFormProps> = ({
  productId,
  productName,
  variantId,
  variantAttributes,
  unitId,
  unitName,
  onTransactionComplete
}) => {
  // Form state
  const [transactionType, setTransactionType] = useState<TransactionType>('addition');
  const [warehouseId, setWarehouseId] = useState('');
  const [destinationWarehouseId, setDestinationWarehouseId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Fetch warehouses on component mount
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const response = await getWarehouses(1, 100);
        setWarehouses(response.warehouses.filter(w => w.status === 'active'));
        
        // Set default warehouse if available
        if (response.warehouses.length > 0) {
          setWarehouseId(response.warehouses[0]._id || '');
          setDestinationWarehouseId(response.warehouses.length > 1 ? response.warehouses[1]._id || '' : '');
        }
      } catch (err) {
        console.error('Error fetching warehouses:', err);
        setError('فشل في تحميل قائمة المستودعات');
      }
    };
    
    fetchWarehouses();
  }, []);
  
  // Format variant attributes for display
  const formatVariantAttributes = () => {
    if (!variantAttributes) return 'النسخة الافتراضية';
    return Object.entries(variantAttributes)
      .map(([key, value]) => `${key}: ${value}`)
      .join('، ');
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Validate input
      if (!warehouseId) {
        throw new Error('يجب اختيار المستودع');
      }
      
      if (transactionType === 'transfer' && !destinationWarehouseId) {
        throw new Error('يجب اختيار المستودع الوجهة للتحويل');
      }
      
      if (transactionType === 'transfer' && warehouseId === destinationWarehouseId) {
        throw new Error('لا يمكن التحويل إلى نفس المستودع');
      }
      
      if (quantity <= 0) {
        throw new Error('يجب أن تكون الكمية أكبر من صفر');
      }
      
      // Create transaction request
      const transactionRequest = {
        type: transactionType,
        warehouseId,
        productId,
        variantId,
        quantity,
        unitId,
        notes,
        // Add destination warehouse for transfers only
        ...(transactionType === 'transfer' && { destinationWarehouseId }),
      };
      
      // Submit transaction
      const response = await createInventoryTransaction(transactionRequest);
      
      // Clear form and show success message
      setQuantity(1);
      setNotes('');
      setSuccess('تمت معالجة معاملة المخزون بنجاح');
      
      // Notify parent component
      onTransactionComplete();
      
    } catch (err: any) {
      console.error('Error creating inventory transaction:', err);
      setError(err.response?.data?.message || err.message || 'فشل في إنشاء معاملة المخزون');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="inventory-transaction-form">
      <h3 className="section-title">إدارة المخزون</h3>
      
      <div className="product-info">
        <div className="product-name">{productName}</div>
        {variantAttributes && (
          <div className="variant-info">{formatVariantAttributes()}</div>
        )}
        <div className="unit-info">الوحدة: {unitName}</div>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="transactionType">نوع المعاملة:</label>
          <select
            id="transactionType"
            value={transactionType}
            onChange={(e) => setTransactionType(e.target.value as TransactionType)}
            required
          >
            <option value="addition">إضافة</option>
            <option value="subtraction">خصم</option>
            <option value="transfer">تحويل</option>
            <option value="adjustment">تعديل</option>
          </select>
        </div>
        
        <div className="form-group">
          <label htmlFor="warehouseId">
            {transactionType === 'transfer' ? 'المستودع المصدر:' : 'المستودع:'}
          </label>
          <select
            id="warehouseId"
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
            required
          >
            <option value="">اختر المستودع</option>
            {warehouses.map(warehouse => (
              <option key={warehouse._id} value={warehouse._id}>
                {warehouse.name} {warehouse.location ? `(${warehouse.location})` : ''}
              </option>
            ))}
          </select>
        </div>
        
        {transactionType === 'transfer' && (
          <div className="form-group">
            <label htmlFor="destinationWarehouseId">المستودع الوجهة:</label>
            <select
              id="destinationWarehouseId"
              value={destinationWarehouseId}
              onChange={(e) => setDestinationWarehouseId(e.target.value)}
              required
            >
              <option value="">اختر المستودع الوجهة</option>
              {warehouses
                .filter(w => w._id !== warehouseId)
                .map(warehouse => (
                  <option key={warehouse._id} value={warehouse._id}>
                    {warehouse.name} {warehouse.location ? `(${warehouse.location})` : ''}
                  </option>
                ))
              }
            </select>
          </div>
        )}
        
        <div className="form-group">
          <label htmlFor="quantity">الكمية:</label>
          <input
            id="quantity"
            type="number"
            min="0.01"
            step="0.01"
            value={quantity}
            onChange={(e) => setQuantity(parseFloat(e.target.value))}
            required
          />
          <span className="unit-label">{unitName}</span>
        </div>
        
        <div className="form-group">
          <label htmlFor="notes">ملاحظات:</label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="أي ملاحظات إضافية عن المعاملة"
            rows={2}
          />
        </div>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        <div className="form-actions">
          <button
            type="submit"
            className="button primary-button"
            disabled={loading}
          >
            {loading ? 'جاري المعالجة...' : 'تنفيذ المعاملة'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InventoryTransactionForm;